import axios, { type AxiosInstance } from 'axios';
import { BaseAPIAdapter } from './BaseAPIAdapter';
import type {
  VideoConfig,
  GenerationResponse,
  ApiResponse,
  AudioGenerationRequest,
  AudioGenerationResponse,
} from '../../types';
import { TIMEOUTS } from '../../constants/timings';
import { firebaseClient } from '../firebase/firebase-client';

import { parseApiError } from '../../utils/apiErrorParser';
import { SCHEMA_FIELDS } from '../../constants/generated-schema';

// Debug flag - set to true to enable verbose logging
// Can be controlled via VITE_DEBUG_API=true in .env.local
const DEBUG_API = import.meta.env.VITE_DEBUG_API === 'true';

/**
 * Adapter for Flask local development API
 * Handles the specific endpoints and data formats of api_server.py
 */
export class FlaskLocalAdapter extends BaseAPIAdapter {
  private apiClient: AxiosInstance;
  protected readonly adapterName = 'FlaskLocalAdapter';

  constructor(baseURL: string, timeout: number = TIMEOUTS.GENERATION_API) {
    super(baseURL, timeout);

    this.apiClient = axios.create({
      baseURL: this.baseURL,
      timeout: this.timeout,
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Auth interceptor: attach Firebase token to every request
    // In local dev without Firebase, getIdToken() returns null and no header is sent
    this.apiClient.interceptors.request.use(async (config) => {
      const token = await firebaseClient.getIdToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Request interceptor for debugging
    this.apiClient.interceptors.request.use(
      (config) => {
        if (DEBUG_API) {
          console.log('[Flask API] Request:', {
            method: config.method?.toUpperCase(),
            url: config.url,
            data: config.data,
          });
        }
        return config;
      },
      (error) => {
        console.error('[Flask API] Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor - unwraps standardized API format {success, data, error}
    this.apiClient.interceptors.response.use(
      (response) => {
        if (DEBUG_API) {
          console.log('[Flask API] Response:', {
            status: response.status,
            data: response.data,
          });
        }

        // Unwrap standardized API format: {success, data, error}
        // After this, response.data contains the actual payload
        if (
          response.data &&
          typeof response.data === 'object' &&
          'success' in response.data &&
          'data' in response.data
        ) {
          // Check for API-level errors
          if (!response.data.success && response.data.error) {
            const error = new Error(response.data.error);
            return Promise.reject(error);
          }
          // Unwrap: response.data becomes the actual data payload
          response.data = response.data.data;
        }

        return response;
      },
      (error) => {
        console.error('[Flask API] Response Error:', {
          status: error.response?.status,
          data: error.response?.data,
          message: error.message,
        });

        if (!error.response) {
          error.message = 'Cannot connect to Flask backend. Please run: python api_server.py';
        }

        return Promise.reject(error);
      }
    );
  }

  async checkHealth(): Promise<ApiResponse<{ status: string; version?: string }>> {
    // Interceptor already unwraps {success, data, error} format
    return this.wrapPassthrough(
      () => this.apiClient.get('/api/v1/health', { timeout: 5000 }),
      'Failed to connect to Flask backend'
    );
  }

  async generateVideo(config: VideoConfig): Promise<GenerationResponse> {
    try {
      // Whitelist: only send fields that exist in the backend VideoGenerationRequest model.
      // The model uses extra="forbid", rejecting any unknown field. Using a whitelist
      // (from SCHEMA_FIELDS) ensures frontend-only form state is never sent.
      // The schema key IS the field name (RULE 1.5).
      const schemaFieldNames = new Set(Object.keys(SCHEMA_FIELDS));
      const apiPayload = Object.fromEntries(
        Object.entries(config).filter(([key]) => schemaFieldNames.has(key))
      );

      // Debug logging to see what's being sent
      if (DEBUG_API) {
        console.log(
          '[FlaskLocalAdapter] Sending config to Flask API:',
          JSON.stringify(apiPayload, null, 2).substring(0, 300) + '...'
        );
        console.log('[FlaskLocalAdapter] Narration mode:', config.narration_mode);
        if (config.narration_mode === 'user_provided') {
          console.log(
            '[FlaskLocalAdapter] User narration text length:',
            config.user_narration_text?.length || 0
          );
        }
      }

      const response = await this.apiClient.post('/api/v1/video-generation/generate', apiPayload);

      if (response.status >= 200 && response.status < 300) {
        // Interceptor already unwraps {success, data, error} format
        const data = response.data;
        // Wire format is job_id (VideoJobSubmissionResponse) — the single canonical
        // id key. Mapped ONCE here to the FE-internal generation_id field.
        const generationId = data.job_id;
        return {
          success: true,
          message: data.message || 'Video generation started',
          generation_id: generationId,
          statusUrl: `/api/v1/jobs/${generationId}`,
        };
      }

      throw new Error('Generation failed');
    } catch (error: unknown) {
      // Enhanced error logging
      console.error('[FlaskLocalAdapter] Video generation error:', error);

      // Check if this is an Axios error with response data containing error_code
      // This handles configuration errors like FOOOCUS_NOT_RUNNING, FOOOCUS_NOT_INSTALLED, etc.
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as {
          response?: { data?: Record<string, unknown>; status?: number };
          message?: string;
        };

        console.error('[FlaskLocalAdapter] Response status:', axiosError.response?.status);
        console.error(
          '[FlaskLocalAdapter] Response data:',
          JSON.stringify(axiosError.response?.data, null, 2)
        );

        const parsed = parseApiError(axiosError);
        if (parsed) {
          return { success: false, ...parsed };
        }
      }

      if (error instanceof Error) {
        throw error;
      }
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      throw new Error(
        axiosError.response?.data?.error || axiosError.message || 'Failed to start video generation'
      );
    }
  }

  async getGenerationStatus(generationId: string): Promise<ApiResponse<Record<string, unknown>>> {
    // Response interceptor already unwrapped the envelope, so unwrapResponse is
    // identity here; mapJobStatusResponse + JOB_NOT_FOUND handling live in the base.
    return this.fetchJobStatus(
      () => this.apiClient.get(`/api/v1/jobs/${generationId}`),
      'Failed to get generation status'
    );
  }

  async generateAudio(config: AudioGenerationRequest): Promise<AudioGenerationResponse> {
    try {
      // Flask API will transform using SchemaTransformer (audio)
      // Send frontend config directly
      if (DEBUG_API) {
        console.log('[FlaskLocalAdapter] Sending audio config:', config);
      }

      const response = await this.apiClient.post('/api/v1/audio/generate', config);

      // Accept any 2xx. The audio route is async and returns 202 Accepted (job
      // queued/started) — the same contract the cloud ServerlessAdapter uses.
      // Previously this only accepted 200/201, so the local 202 was rejected with
      // a misleading "Audio generation failed" even though the job had started.
      if (response.status >= 200 && response.status < 300) {
        // Interceptor already unwraps {success, data, error} format
        const data = response.data;
        return {
          success: true,
          // Wire format is job_id — the single canonical id key (same shape as
          // the cloud route). Mapped ONCE here to the FE-internal generation_id.
          generation_id: data.job_id,
          audio_url: data.audio_url,
          message: data.message || 'Audio generation started',
        };
      }

      throw new Error('Audio generation failed');
    } catch (error: unknown) {
      console.error('[FlaskLocalAdapter] Audio generation error:', error);
      if (error instanceof Error) {
        throw error;
      }
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      throw new Error(
        axiosError.response?.data?.error || axiosError.message || 'Failed to start audio generation'
      );
    }
  }

  async getAudioStatus(generationId: string): Promise<ApiResponse<Record<string, unknown>>> {
    // Audio status uses same endpoint as video status; interceptor already
    // unwraps the {success, data, error} format.
    return this.wrapPassthrough(
      () => this.apiClient.get(`/api/v1/jobs/${generationId}`),
      'Failed to get audio generation status'
    );
  }
}
